import React, { useEffect, useState} from "react";
import {
  Card,
  Container,
  Row
} from "reactstrap";
import { useNotification } from "providers/NotificationProvider";
import userServices from "services/userServices";
import SupportTicketsTable from "./SupportTicketsTable";
import DeleteModal from "../../../modals/DeleteModal";
import SimpleHeader from "components/Headers/SimpleHeader.js";
import { useTranslation } from "context/TranslationProvider";

const AllSupportTickets = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const { t } = useTranslation();
  const [itemToDelete, setItemToDelete] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const { notify } = useNotification();
  const [statusLoadingId, setStatusLoadingId] = useState(null);
  const [tableState, setTableState] = useState({
    page: 1,
    limit: 20,
    sortBy: "title",
    sortOrder: 1,
    search: ""
  });

  const [meta, setMeta] = useState({
    totalItems: 0,
    totalPages: 0,
    page: 1,
    limit: 20,
    sortBy: "title",
    sortOrder: 1
  });

  const getAllTickets = React.useCallback((state) => {
  setLoading(true);
  const payload = {
    page: state.page,
    limit: state.limit,
    sortBy: state.sortBy,
    sortOrder: state.sortOrder,
    search: state.search
  };

  userServices
    .fetchSupportRequests(payload)
    .then((res) => {
      const payloadObj = res?.data || res?.requests || res;
      const requests = Array.isArray(payloadObj) ? payloadObj : (payloadObj.requests || []);
      const pagination = res?.pagination || res || {};

      setItems(requests);
      setMeta({
        totalItems: pagination?.total || pagination?.totalItems || 0,
        totalPages: pagination?.totalPages || 0,
        page: state.page,
        limit: state.limit,
        sortBy: state.sortBy,
        sortOrder: state.sortOrder
      });
    })
    .catch(() => {
      notify("error", "Failed to fetch support tickets");
    })
    .finally(() => setLoading(false));
}, [notify]); 

  const deleteRow = (item) => {
    setItemToDelete(item);
    setOpenModal(true);
  };

  const handleModalClose = () => {
    setOpenModal(false);
    setItemToDelete(null);
  };

  const handleTableChange = (type, payload) => {
    if (type === "sort") {
      const { sortField, sortOrder } = payload;
      setTableState((prev) => ({
        ...prev,
        sortBy: sortField,
        sortOrder: sortOrder === "asc" ? 1 : -1,
        page: 1
      }));

    } else if (type === "pagination") {
      const { page, sizePerPage } = payload;

      setTableState((prev) => {
        const limitChanged = prev.limit !== sizePerPage;
        return {
          ...prev,
          page: limitChanged ? 1 : page,
          limit: sizePerPage
        };
      });
    } else if (type === "refresh") {
      getAllTickets(tableState);
    }
  };

  const handleSearch = (value) => {
    setTableState((prev) => ({
      ...prev,
      page: 1,
      search: value
    }));
  };

  const queryState = React.useMemo(() => ({
    page: tableState.page,
    limit: tableState.limit,
    sortBy: tableState.sortBy,
    sortOrder: tableState.sortOrder,
    search: tableState.search
  }), [tableState]);

  useEffect(() => {
    getAllTickets(queryState);
  }, [queryState, getAllTickets]);

  const removeRequest = () => {
    if (!itemToDelete?._id) return;

    setStatusLoadingId(itemToDelete._id);
    userServices
      .deleteSupportRequest({ deletedListingId: itemToDelete._id })
      .then((res) => {
        const payload = res?.data || res;
        if (payload?.success) {
          notify("success", t(payload?.messageKey || "page.support-tickets.toast.deleteSuccess", payload?.message || t("page.support-tickets.toast.deleteSuccess", "Support ticket deleted successfully")));
          getAllTickets(tableState);
        } else {
          notify("error", t(payload?.messageKey || payload?.error || "page.support-tickets.toast.deleteFailed", payload?.message || t("page.support-tickets.toast.deleteFailed", "Failed to delete support ticket")));
        }
      })
      .catch((err) => {
        const errPayload = err?.response?.data || {};
        notify("error", t(errPayload?.messageKey || errPayload?.error || "page.support-tickets.toast.fetchFailed", errPayload?.message || t("page.support-tickets.toast.fetchFailed", "Failed to fetch support tickets")));
      })
      .finally(() => {
        setStatusLoadingId(null);
        handleModalClose();
      });
  };

  const getTicketLabel = (source) =>
    source === "Dispute" ? "dispute" : "support-request";


  return (
    <div className="card-header-full-height">
      <SimpleHeader name={t("sideNav.supportTickets", "Support Tickets")} parentName={t("sideNav.dashboard", "Dashboard")} />
      <Container className="mt--6" fluid>
        <Row>
          <div className="col">
            <Card className="shadow">
                <SupportTicketsTable
                items={items}
                setItems={setItems}
                loading={loading}
                meta={meta}
                tableState={tableState}
                search={tableState.search}
                onSearch={handleSearch}
                onTableChange={handleTableChange}
                deleteRow={deleteRow}
                statusLoadingId={statusLoadingId}
              />
            </Card>
          </div>
        </Row>
      </Container>

      <DeleteModal
        isOpen={openModal}
        toggle={handleModalClose}
        itemName={itemToDelete ? getTicketLabel(itemToDelete.source) : "ticket"}
        onDelete={removeRequest}
        loading={statusLoadingId !== null}
      />
    </div>
  );
};

export default AllSupportTickets;
